import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { querySuites } from '@/features/suites/data/suites';
import {
  buildSuiteHistoryQueryInput,
  buildSuiteHistoryQueryStateKey,
} from '@/features/suites/pages/suiteHistoryQueries';
import { buildSuiteHistoryRows } from '@/features/suites/pages/suiteHistoryRows';
import { queryTests } from '@/features/tests/data/tests';
import { fetchAllQueryPages } from '@/shared/data/fetchAllQueryPages';
import { gridPaths, queryKeys } from '@/shared/hooks';
import { buildInFilter } from '@/ui/composites/grid/gridFilters';
import type { DailyHistorySortKey } from '@/features/shared/components/EntityDailyHistoryPanel.types';
import type { SuiteExecutionStatus } from '@flowqa/shared';

type SuiteHistoryDateWindow = {
  startDay: string | null;
  endDay: string | null;
  previousStartDay: string | null;
  previousEndDay: string | null;
};

type UseSuiteHistoryPanelDataArgs = {
  cloneDateQuickFilter: string;
  cloneDateWindow: SuiteHistoryDateWindow;
  cloneSearchValue: string;
  cloneFilterStatuses: SuiteExecutionStatus[];
  cloneFilterSuites: string[];
  cloneFilterTags: string[];
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
  showCloneDeltas: boolean;
};

type UseSuiteHistoryPanelDataResult = {
  dailyHistoryRows: ReturnType<typeof buildSuiteHistoryRows>;
  historyPage: number;
  historyPageSize: number;
  historySortDirection: 'asc' | 'desc';
  historySortKey: DailyHistorySortKey;
  dailyHistoryLoading: boolean;
  totalCount: number;
  setHistoryPage: (nextPage: number) => void;
  setHistoryPageSize: (nextPageSize: number) => void;
  setHistorySortDirection: (nextSortDirection: 'asc' | 'desc') => void;
  setHistorySortKey: (nextSortKey: DailyHistorySortKey) => void;
};

export const useSuiteHistoryPanelData = ({
  cloneDateQuickFilter,
  cloneDateWindow,
  cloneSearchValue,
  cloneFilterStatuses,
  cloneFilterSuites,
  cloneFilterTags,
  selectedOrganisationId,
  selectedProjectId,
  showCloneDeltas,
}: UseSuiteHistoryPanelDataArgs): UseSuiteHistoryPanelDataResult => {
  const [historyPage, setHistoryPage] = useState(0);
  const [historyPageSize, setHistoryPageSize] = useState(5);
  const [historySortKey, setHistorySortKey] = useState<DailyHistorySortKey>('name');
  const [historySortDirection, setHistorySortDirection] = useState<'asc' | 'desc'>('asc');

  const dailyHistoryQueryKey = useMemo(
    () =>
      buildSuiteHistoryQueryStateKey({
        cloneDateQuickFilter,
        cloneDateWindowEndDay: cloneDateWindow.endDay,
        cloneDateWindowPreviousEndDay: cloneDateWindow.previousEndDay,
        cloneDateWindowPreviousStartDay: cloneDateWindow.previousStartDay,
        cloneDateWindowStartDay: cloneDateWindow.startDay,
        cloneSearchValue,
        cloneFilterStatuses,
        cloneFilterSuites,
        cloneFilterTags,
        historySortDirection,
        historySortKey,
        historyPage,
        historyPageSize,
        selectedOrganisationId,
        selectedProjectId,
      }),
    [
      cloneDateQuickFilter,
      cloneDateWindow.endDay,
      cloneDateWindow.previousEndDay,
      cloneDateWindow.previousStartDay,
      cloneDateWindow.startDay,
      cloneSearchValue,
      cloneFilterStatuses,
      cloneFilterSuites,
      cloneFilterTags,
      historySortDirection,
      historySortKey,
      historyPage,
      historyPageSize,
      selectedOrganisationId,
      selectedProjectId,
    ],
  );

  const dailyHistoryQuery = useQuery({
    queryKey: queryKeys.dataGrid(gridPaths.suites, 'daily-history', dailyHistoryQueryKey),
    queryFn: async () =>
      await querySuites(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        buildSuiteHistoryQueryInput({
          cloneDateWindowEndDay: cloneDateWindow.endDay,
          cloneDateWindowPreviousEndDay: cloneDateWindow.previousEndDay,
          cloneDateWindowPreviousStartDay: cloneDateWindow.previousStartDay,
          cloneDateWindowStartDay: cloneDateWindow.startDay,
          cloneSearchValue,
          cloneFilterStatuses,
          cloneFilterSuites,
          cloneFilterTags,
          historySortDirection,
          historySortKey,
          historyPage,
          historyPageSize,
          showCloneDeltas,
        }),
      ),
    enabled: selectedProjectId !== null,
  });

  const visibleSuiteIds = useMemo(
    () =>
      (dailyHistoryQuery.data?.items ?? [])
        .map((suite) => suite.id)
        .sort((left, right) => left.localeCompare(right)),
    [dailyHistoryQuery.data?.items],
  );

  const suiteHistoryTestsQueryKey = useMemo(
    () =>
      JSON.stringify({
        cloneDateWindowStartDay: cloneDateWindow.startDay,
        cloneDateWindowEndDay: cloneDateWindow.endDay,
        cloneSearchValue,
        selectedOrganisationId,
        selectedProjectId,
        suiteIds: visibleSuiteIds,
      }),
    [
      cloneDateWindow.endDay,
      cloneDateWindow.startDay,
      cloneSearchValue,
      selectedOrganisationId,
      selectedProjectId,
      visibleSuiteIds,
    ],
  );

  const suiteHistoryTestsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      gridPaths.suites,
      'daily-history-tests',
      suiteHistoryTestsQueryKey,
    ),
    queryFn: async () =>
      await fetchAllQueryPages(
        async ({ page, pageSize }) =>
          await queryTests(selectedProjectId, selectedOrganisationId ?? undefined, {
            page,
            pageSize,
            search: cloneSearchValue,
            sorts: [{ field: 'name', direction: 'asc' }],
            filters: buildInFilter('suiteId', visibleSuiteIds),
            metricsStartDay: cloneDateWindow.startDay ?? undefined,
            metricsEndDay: cloneDateWindow.endDay ?? undefined,
            include: {
              suites: { ids: true, items: true, primary: true },
              dailyHistory: true,
            },
          }),
      ),
    enabled: selectedProjectId !== null && visibleSuiteIds.length > 0,
  });

  const dailyHistoryRows = useMemo(
    () => buildSuiteHistoryRows(dailyHistoryQuery.data?.items, suiteHistoryTestsQuery.data?.items),
    [dailyHistoryQuery.data?.items, suiteHistoryTestsQuery.data?.items],
  );

  useEffect(() => {
    setHistoryPage(0);
  }, [
    cloneDateQuickFilter,
    cloneDateWindow.endDay,
    cloneDateWindow.previousEndDay,
    cloneDateWindow.previousStartDay,
    cloneDateWindow.startDay,
    cloneSearchValue,
    cloneFilterStatuses,
    cloneFilterSuites,
    cloneFilterTags,
    selectedOrganisationId,
    selectedProjectId,
  ]);

  return {
    dailyHistoryRows,
    historyPage,
    historyPageSize,
    historySortDirection,
    historySortKey,
    dailyHistoryLoading: dailyHistoryQuery.isFetching || suiteHistoryTestsQuery.isFetching,
    totalCount: dailyHistoryQuery.data?.total ?? 0,
    setHistoryPage,
    setHistoryPageSize,
    setHistorySortDirection,
    setHistorySortKey,
  };
};
